import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronsRight, Pause } from 'lucide-react';
import { CONSTANTS } from '../../data';
import { CardType, GameMode, PlayerPosition, PlayersType, ScoresType, StealableStack } from '../../types';
import PlayerZone from './PlayerZone';
import Card from './Card';

interface GameUIProps {
  // State
  deck: CardType[];
  players: PlayersType;
  scores: ScoresType;
  drawnCard: CardType | null;
  bustingPlayer: PlayerPosition | null;
  bustingCards: CardType[];
  stealableStacks: StealableStack[];
  showStealMessage: boolean;
  isGameOver: boolean;
  speedLevel: 1 | 2 | 4;
  isHumanTurn: boolean;
  isDrawing: boolean;
  isScoring: boolean;
  isPaused: boolean;
  gameMode: GameMode;
  activePlayerPosition: PlayerPosition;
  activePlayerPositions: PlayerPosition[];
  playerNames: Record<PlayerPosition, string>;
  cardBeingPlaced: string | null;
  
  // Timing
  bustAnimationTime: number;

  // Handlers
  onDrawCard: () => void;
  onEndTurn: () => void;
  onSteal: () => void;
  onPause: () => void;
  onToggleSpeed: () => void;
}

const GameUI = (props: GameUIProps) => {
  const {
    deck, players, scores, drawnCard, bustingPlayer, bustingCards, stealableStacks,
    showStealMessage, isGameOver, speedLevel, isHumanTurn, isDrawing, isScoring,
    isPaused, gameMode, activePlayerPosition, activePlayerPositions, playerNames, cardBeingPlaced, bustAnimationTime,
    onDrawCard, onEndTurn, onSteal, onPause, onToggleSpeed
  } = props;

  const canDrawCard = !isDrawing && deck.length > 0 && !drawnCard && !bustingPlayer && !isScoring && !isGameOver && isHumanTurn && !isPaused;
  const canEndTurn = !isDrawing && !bustingPlayer && !isScoring && !isGameOver && isHumanTurn && !isPaused;

  // Calculate initial position for busting cards based on player position
  const getBustingCardInitialPosition = (position: PlayerPosition) => {
    switch (position) {
      case 'top':
        return { x: '50%', y: '20%' };
      case 'bottom':
        return { x: '50%', y: '80%' };
      case 'left':
        return { x: '20%', y: '50%' };
      case 'right':
        return { x: '80%', y: '50%' };
      default:
        return { x: '50%', y: '50%' };
    }
  };

  return (
    <>
      {/* PAUSE BUTTON */}
      <AnimatePresence>
        {!isGameOver && !isPaused && (
          <motion.div
            className="absolute top-6 right-6 z-[100]"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <motion.button
              onClick={onPause}
              className="flex items-center justify-center w-14 h-14 rounded-full font-bold text-base transition-all duration-300 shadow-lg bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-600 hover:to-slate-700 cursor-pointer shadow-slate-600/50 hover:shadow-slate-600/70 hover:shadow-xl border-2 border-slate-500"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Pause size={24} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Player Zones */}
      {activePlayerPositions.map(pos => (
        <PlayerZone
          key={pos}
          position={pos}
          name={playerNames[pos]}
          score={scores[pos]}
          cardStacks={players[pos]}
          isActive={activePlayerPosition === pos && !isScoring && !isGameOver}
          cardBeingPlacedId={cardBeingPlaced}
          isBusting={bustingPlayer === pos}
          bustingCards={bustingPlayer === pos ? bustingCards : []}
          stealableStacks={stealableStacks}
          gameMode={gameMode}
          bustAnimationDuration={bustAnimationTime}
          speedLevel={speedLevel}
        />
      ))}

      {/* Busting cards animation - rendered at root level */}
      <AnimatePresence>
        {bustingPlayer && bustingCards.map((card, index) => {
          const initialPos = getBustingCardInitialPosition(bustingPlayer);
          return (
            <motion.div
              key={`busting-${card.id}`}
              className="absolute pointer-events-none"
              style={{
                left: initialPos.x,
                top: initialPos.y,
                transform: 'translate(-50%, -50%)',
                zIndex: 1000
              }}
              initial={{ scale: 1, opacity: 1 }}
              animate={{ 
                scale: 0,
                opacity: 0,
                x: Math.random() * 400 - 200,
                y: Math.random() * 400 - 200,
                rotate: Math.random() * 360
              }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ 
                duration: bustAnimationTime,
                delay: index * (speedLevel > 1 ? 0.02 : 0.1),
                ease: "easeOut"
              }}
            >
              <Card card={card} />
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Center Table Area */}
      <div className="flex flex-col items-center justify-center gap-8 relative z-10">
        {/* Drawn Card Area */}
        <div className="w-16 h-24 flex items-center justify-center">
          <AnimatePresence>
            {drawnCard && (
              <motion.div
                layoutId={drawnCard.id}
                initial={{ scale: 0, rotate: 180, y: -50 }}
                animate={{ scale: 1, rotate: 0, y: 0 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.6, type: "spring", stiffness: 200, damping: 15 }}
              >
                <Card card={drawnCard} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Deck and Controls */}
        <div className="flex flex-col items-center gap-6">
          {/* Deck */}
          <motion.div
            className={`relative group transition-all duration-300 ${canDrawCard ? 'hover:scale-105 cursor-pointer' : 'opacity-60'}`}
            whileHover={canDrawCard ? { y: -5 } : {}}
            onClick={canDrawCard ? onDrawCard : undefined}
          >
            {deck.length > 0 && (
              <>
                <Card isFaceDown />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-black/70 to-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none backdrop-blur-sm">
                  <span className="font-bold text-3xl text-white drop-shadow-lg">{deck.length}</span>
                  <span className="text-sm text-white/90 font-medium">{CONSTANTS.UI_TEXT.CARDS_LABEL}</span>
                </div>
              </>
            )}
          </motion.div>
          
          {/* Control Buttons */}
          <div className="flex items-center gap-6">
            <motion.button
              onClick={onDrawCard}
              disabled={!canDrawCard}
              className={`px-6 py-3 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg ${
                canDrawCard
                  ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-300 hover:to-yellow-400 cursor-pointer shadow-yellow-400/50 hover:shadow-yellow-400/70 hover:shadow-xl'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              whileHover={canDrawCard ? { scale: 1.05, y: -2 } : {}}
              whileTap={canDrawCard ? { scale: 0.95 } : {}}
            >
              {CONSTANTS.UI_TEXT.DRAW_CARD_BUTTON}
            </motion.button>
            
            <motion.button
              onClick={onEndTurn}
              disabled={!canEndTurn}
              className={`px-6 py-3 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg ${
                canEndTurn
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-400 hover:to-blue-500 cursor-pointer shadow-blue-500/50 hover:shadow-blue-500/70 hover:shadow-xl'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              whileHover={canEndTurn ? { scale: 1.05, y: -2 } : {}}
              whileTap={canEndTurn ? { scale: 0.95 } : {}}
            >
              {CONSTANTS.UI_TEXT.END_TURN_BUTTON}
            </motion.button>
            
            {/* STEAL BUTTON */}
            {stealableStacks.length > 0 && !isGameOver && isHumanTurn && (
              <motion.button
                onClick={onSteal}
                disabled={!canEndTurn}
                className={`px-6 py-3 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg ${
                  canEndTurn
                    ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white hover:from-pink-400 hover:to-pink-500 cursor-pointer shadow-pink-500/50 hover:shadow-pink-500/70 hover:shadow-xl'
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 cursor-not-allowed opacity-50'
                }`}
                whileHover={canEndTurn ? { scale: 1.05, y: -2 } : {}}
                whileTap={canEndTurn ? { scale: 0.95 } : {}}
                animate={canEndTurn ? { 
                  boxShadow: [
                    "0 10px 25px rgba(236, 72, 153, 0.3)",
                    "0 10px 35px rgba(236, 72, 153, 0.5)",
                    "0 10px 25px rgba(236, 72, 153, 0.3)"
                  ]
                } : {}}
                transition={{ repeat: canEndTurn ? Infinity : 0, duration: canEndTurn ? 1.5 : 0.3 }}
              >
                ⚡ {CONSTANTS.UI_TEXT.STEAL_BUTTON}
              </motion.button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        <div className="h-12 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {bustingPlayer && (
              <motion.div
                key="bust"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="text-center"
              >
                <p className="text-2xl font-bold text-red-400 animate-pulse bg-red-900/30 px-6 py-3 rounded-xl border border-red-500/50">
                  💥 {playerNames[bustingPlayer]} {CONSTANTS.UI_TEXT.PLAYER_BUSTED}!
                </p>
              </motion.div>
            )}
            {deck.length === 0 && !drawnCard && !bustingPlayer && !isGameOver && (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="text-center"
              >
                <p className="text-2xl font-bold bg-yellow-900/30 px-6 py-3 rounded-xl border border-yellow-500/50">
                  🃏 {CONSTANTS.UI_TEXT.DECK_EMPTY}
                </p>
              </motion.div>
            )}
            {showStealMessage && (
              <motion.div
                key="steal"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="text-center"
              >
                <p className="text-2xl font-bold text-pink-300 bg-pink-900/30 px-6 py-3 rounded-xl border border-pink-500/50">
                  ⚡ {CONSTANTS.UI_TEXT.STEAL_MESSAGE}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* SPEED UP BUTTON */}
      <AnimatePresence>
        {gameMode === 'vs_cpu' && !isHumanTurn && !isGameOver && !isPaused && (
          <motion.div
            className="absolute bottom-6 right-6 z-[100]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <motion.button
              onClick={onToggleSpeed}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-400 hover:to-blue-500 cursor-pointer shadow-blue-500/50 hover:shadow-blue-500/70 hover:shadow-xl border-2 border-blue-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {speedLevel === 4 ? (
                <>
                  <Play size={20} />
                  <span>Normal Speed (x1)</span>
                </>
              ) : (
                <>
                  <ChevronsRight size={20} />
                  <span>Speed Up (x{speedLevel * 2})</span>
                </>
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GameUI;
